import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, MapPin, Video, Users, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const EMPTY = { baslik: "", aciklama: "", tarih: "", yer: "", tur: "Online", link: "", kapasite: 0 };

function EventCard({ e, onRsvp, onDelete, isStaff }) {
  return (
    <div className={`border rounded-xl p-5 bg-surface ${e.gecti ? "opacity-70 border-line" : "border-terra/20"}`} data-testid={`event-${e.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-wider text-terra flex items-center gap-1">
            {e.tur === "Online" ? <Video size={13} /> : <MapPin size={13} />} {e.tur}
          </span>
          {e.kulup_adi && <p className="text-xs text-gold mt-0.5">🔖 {e.kulup_adi} kulübü</p>}
          <h3 className="font-serif text-2xl text-ink mt-1">{e.baslik}</h3>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><CalendarDays size={14} /> {fmtDate(e.tarih)}</p>
        </div>
        {isStaff && <button onClick={() => onDelete(e.id)} data-testid={`del-event-${e.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>}
      </div>
      {e.aciklama && <p className="text-sm text-ink-2 mt-3 leading-relaxed">{e.aciklama}</p>}
      {e.yer && <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1"><MapPin size={13} /> {e.yer}</p>}
      <div className="flex items-center justify-between mt-4">
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Users size={13} /> {e.katilimci_sayisi} katılımcı</span>
        <div className="flex gap-2">
          {e.link && <a href={e.link} target="_blank" rel="noreferrer" className="text-sm text-terra hover:underline flex items-center gap-1"><ExternalLink size={13} /> Bağlantı</a>}
          {!e.gecti && (
            <Button size="sm" onClick={() => onRsvp(e.id)} data-testid={`rsvp-${e.id}`}
              className={e.katildim ? "bg-sage hover:bg-sage/90 text-white" : "bg-terra hover:bg-terra-hover text-white"}>
              {e.katildim ? "Katılıyorsun ✓" : "Katıl (+25 AP)"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const { user } = useAuth();
  const isStaff = ["admin", "moderator"].includes(user?.role);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/events").then((r) => setEvents(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const rsvp = async (id) => {
    try { await api.post(`/events/${id}/rsvp`); load(); toast.success("Katılım durumun güncellendi."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Etkinlik silinsin mi?")) return; await api.delete(`/admin/events/${id}`); load(); };
  const create = async () => {
    if (!form.baslik.trim() || !form.tarih) { toast.error("Başlık ve tarih gerekli."); return; }
    try {
      await api.post("/admin/events", { ...form, kapasite: Number(form.kapasite) || 0, tarih: new Date(form.tarih).toISOString() });
      setOpen(false); setForm(EMPTY); load(); toast.success("Etkinlik oluşturuldu.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const upcoming = events.filter((e) => !e.gecti);
  const past = events.filter((e) => e.gecti);

  return (
    <div className="max-w-3xl mx-auto" data-testid="events-page">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra">Topluluk</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">Etkinlikler & Okuma Kulüpleri</h1>
        </div>
        {isStaff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="create-event-btn" className="bg-terra hover:bg-terra-hover text-white gap-1 rounded-full"><Plus size={16} /> Etkinlik</Button></DialogTrigger>
            <DialogContent className="bg-surface max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Yeni Etkinlik</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Başlık</Label><Input data-testid="event-title" value={form.baslik} onChange={(e) => setForm({ ...form, baslik: e.target.value })} className="mt-1" /></div>
                <div><Label>Açıklama</Label><Textarea value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tarih & Saat</Label><Input data-testid="event-date" type="datetime-local" value={form.tarih} onChange={(e) => setForm({ ...form, tarih: e.target.value })} className="mt-1" /></div>
                  <div><Label>Tür</Label>
                    <Select value={form.tur} onValueChange={(v) => setForm({ ...form, tur: v })}>
                      <SelectTrigger className="mt-1" data-testid="event-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Online">Online</SelectItem><SelectItem value="Yüz Yüze">Yüz Yüze</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Yer / Adres</Label><Input value={form.yer} onChange={(e) => setForm({ ...form, yer: e.target.value })} className="mt-1" placeholder="Google Maps adresi veya salon" /></div>
                <div><Label>Bağlantı (Zoom / Harita)</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1" placeholder="https://zoom.us/..." /></div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="event-save" className="bg-terra hover:bg-terra-hover text-white">Oluştur</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <section className="mt-8 space-y-4">
        <h2 className="font-serif text-2xl text-ink">Yaklaşan Etkinlikler</h2>
        {upcoming.length ? upcoming.map((e) => <EventCard key={e.id} e={e} onRsvp={rsvp} onDelete={del} isStaff={isStaff} />) : <p className="text-muted-foreground text-sm">Şu an planlanmış etkinlik yok.</p>}
      </section>
      {past.length > 0 && (
        <section className="mt-10 space-y-4">
          <h2 className="font-serif text-2xl text-ink">Geçmiş Etkinlikler</h2>
          {past.map((e) => <EventCard key={e.id} e={e} onRsvp={rsvp} onDelete={del} isStaff={isStaff} />)}
        </section>
      )}
    </div>
  );
}
